import { create } from 'zustand';
import authFetch from '../utils/authFetch';
import useBoardStore from './boardStore';
import { clearDraft } from '../utils/commentDrafts';
import { dialog } from './dialogStore';

function bumpCardCommentCount(cardId, delta) {
  const state = useBoardStore.getState();
  useBoardStore.setState({
    cards: state.cards.map((c) =>
      c.id === cardId ? { ...c, comment_count: Math.max(0, (c.comment_count || 0) + delta) } : c
    ),
  });
}

const useCardPanelStore = create((set, get) => ({
  openCardId: null,
  activeTab: 'updates',
  comments: [],
  loadingComments: false,
  attachments: [],
  loadingAttachments: false,
  activity: [],
  loadingActivity: false,
  // Erro guardado da última tentativa de fetchActivity (ex.: 403 por falta
  // de permissão). null = sem erro. Distinto de activity=[] (que também
  // representa "carregado, sem itens").
  activityError: null,
  // Paginação por cursor (M0): next_cursor/has_more vêm de meta na resposta.
  activityNextCursor: null,
  activityHasMore: false,
  loadingMoreActivity: false,
  uploadProgress: null,

  // Editores de comentário com texto não enviado (key de rascunho -> true).
  // Fonte da verdade em tempo real do "há texto não enviado".
  dirtyEditors: {},
  // Reentrância: evita abrir dois popups de descarte ao mesmo tempo
  // (ex.: Esc seguido de clique no backdrop).
  confirmingDiscard: false,
  // Flag lida pelo onFlush/cleanup dos editores: durante um descarte
  // consciente, NÃO regravar o rascunho que acabamos de limpar.
  discarding: false,

  // Cada editor reporta se está sujo (texto não-vazio) e qual sua key de rascunho.
  setEditorDirty: (key, dirty) => {
    const cur = get().dirtyEditors;
    const has = !!cur[key];
    if (dirty === has) return; // sem mudança real -> evita re-render
    const next = { ...cur };
    if (dirty) next[key] = true;
    else delete next[key];
    set({ dirtyEditors: next });
  },
  // Remove o registro de um editor (no unmount/envio/cancelamento).
  unregisterEditor: (key) => {
    const cur = get().dirtyEditors;
    if (!(key in cur)) return;
    const next = { ...cur };
    delete next[key];
    set({ dirtyEditors: next });
  },
  hasUnsavedComment: () => Object.keys(get().dirtyEditors).length > 0,

  openCard: (cardId) => {
    set({
      openCardId: cardId, activeTab: 'updates', comments: [], attachments: [], activity: [],
      activityError: null, activityNextCursor: null, activityHasMore: false, loadingMoreActivity: false,
      dirtyEditors: {}, confirmingDiscard: false, discarding: false,
    });
    if (cardId) {
      get().fetchComments(cardId);
      get().fetchAttachments(cardId);
      get().fetchActivity(cardId);
    }
  },
  // Fecha o painel. NÃO reseta `discarding`: no descarte, os editores ainda
  // vão desmontar (e disparar onFlush) no próximo ciclo — a flag precisa
  // continuar true até lá. openCard reseta na reabertura.
  closeCard: () => set({
    openCardId: null, comments: [], attachments: [], activity: [],
    activityError: null, activityNextCursor: null, activityHasMore: false, loadingMoreActivity: false,
    dirtyEditors: {}, confirmingDiscard: false,
  }),

  // Interceptação do fechamento (X / backdrop / Esc). Se houver texto não
  // enviado, pede confirmação; senão fecha direto (instantâneo, sem popup).
  // Reusa o DialogHost: Esc/backdrop/X do popup = cancelar = "continuar
  // editando" (não descarta), atendendo ao requisito de Esc duplo seguro.
  requestCloseCard: async () => {
    if (!get().hasUnsavedComment()) { get().closeCard(); return; }
    if (get().confirmingDiscard) return; // já há um popup aberto
    set({ confirmingDiscard: true });
    let discard;
    try {
      discard = await dialog.confirm(
        'Descartar atualização?',
        'Você tem texto não enviado. Se descartar, ele será perdido.',
        { confirmLabel: 'Descartar', cancelLabel: 'Continuar editando', danger: true },
      );
    } finally {
      // Se o card já fechou por outro caminho enquanto o popup estava aberto,
      // não reabrir nada — apenas baixar a flag.
      if (get().openCardId == null) { set({ confirmingDiscard: false }); return; }
      set({ confirmingDiscard: false });
    }
    if (!discard) return; // "Continuar editando": mantém tudo intacto
    // Descarte consciente: suprime o flush dos editores e limpa os rascunhos
    // sujos ANTES de desmontar, para o draft não ser ressuscitado no unmount.
    const keys = Object.keys(get().dirtyEditors);
    set({ discarding: true });
    for (const key of keys) clearDraft(key);
    get().closeCard(); // desmonta os editores (onFlush ainda vê discarding=true)
    // Baixa a flag só após o desmonte + flushes síncronos do unmount terem
    // rodado. Reabrir o card antes disso já reseta via openCard.
    setTimeout(() => set({ discarding: false }), 0);
  },
  // Lido pelo onFlush dos editores no unmount.
  isDiscarding: () => get().discarding,

  setTab: (activeTab) => set({ activeTab }),

  fetchAttachments: async (cardId) => {
    set({ loadingAttachments: true });
    try {
      const attachments = await authFetch(`/api/v1/cards/${cardId}/attachments`);
      set({ attachments });
    } catch (err) {
      console.error('fetchAttachments failed:', err);
      set({ attachments: [] });
    } finally {
      set({ loadingAttachments: false });
    }
  },

  uploadAttachment: (cardId, file) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append('file', file);
    xhr.open('POST', `/api/v1/cards/${cardId}/attachments`);
    const token = localStorage.getItem('token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) set({ uploadProgress: Math.round((e.loaded / e.total) * 100) });
    };
    xhr.onload = () => {
      set({ uploadProgress: null });
      try {
        const data = JSON.parse(xhr.responseText);
        if (!data.success) return reject(new Error(data.error || 'Upload failed'));
        set((s) => ({ attachments: [data.data, ...s.attachments] }));
        resolve(data.data);
      } catch (e) { reject(e); }
    };
    xhr.onerror = () => { set({ uploadProgress: null }); reject(new Error('Network error')); };
    xhr.send(form);
  }),

  deleteAttachment: async (id) => {
    await authFetch(`/api/v1/attachments/${id}`, { method: 'DELETE' });
    set((s) => ({ attachments: s.attachments.filter((a) => a.id !== id) }));
  },

  fetchActivity: async (cardId) => {
    set({ loadingActivity: true, activityError: null, activityNextCursor: null, activityHasMore: false });
    try {
      const res = await authFetch(`/api/v1/cards/${cardId}/activity`, { full: true });
      set({ activity: res.data, activityNextCursor: res.meta?.next_cursor ?? null, activityHasMore: !!res.meta?.has_more });
    } catch (err) {
      console.error('fetchActivity failed:', err);
      set({ activity: [], activityError: { status: err.status, message: err.message }, activityNextCursor: null, activityHasMore: false });
    } finally {
      set({ loadingActivity: false });
    }
  },

  // "Carregar mais": busca a próxima página pelo cursor guardado e acrescenta
  // no fim da lista já carregada (nunca substitui). Reentrância e fim de
  // paginação resolvidos pelos próprios guards (sem has_more/cursor ou já
  // carregando, não faz nada).
  fetchMoreActivity: async (cardId) => {
    const { activityHasMore, activityNextCursor, loadingMoreActivity } = get();
    if (!activityHasMore || !activityNextCursor || loadingMoreActivity) return;
    set({ loadingMoreActivity: true });
    try {
      const res = await authFetch(`/api/v1/cards/${cardId}/activity?cursor=${encodeURIComponent(activityNextCursor)}`, { full: true });
      set((s) => ({
        activity: [...s.activity, ...res.data],
        activityNextCursor: res.meta?.next_cursor ?? null,
        activityHasMore: !!res.meta?.has_more,
      }));
    } catch (err) {
      console.error('fetchMoreActivity failed:', err);
    } finally {
      set({ loadingMoreActivity: false });
    }
  },

  fetchComments: async (cardId) => {
    set({ loadingComments: true });
    try {
      const comments = await authFetch(`/api/v1/cards/${cardId}/comments`);
      set({ comments });
    } catch (err) {
      console.error('fetchComments failed:', err);
      set({ comments: [] });
    } finally {
      set({ loadingComments: false });
    }
  },

  uploadCommentAsset: (cardId, file, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append('file', file);
    xhr.open('POST', `/api/v1/cards/${cardId}/comment-asset`);
    const token = localStorage.getItem('token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (!data.success) return reject(new Error(data.error || 'Upload failed'));
        resolve(data.data);
      } catch (e) { reject(e); }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(form);
  }),

  addComment: async (cardId, content, parent_id = null, files = [], assetIds = []) => {
    let comment;
    if (!files || files.length === 0) {
      comment = await authFetch(`/api/v1/cards/${cardId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content, parent_id, assetIds }),
      });
    } else {
      comment = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const form = new FormData();
        form.append('content', content || '');
        if (parent_id) form.append('parent_id', String(parent_id));
        if (assetIds.length) form.append('assetIds', JSON.stringify(assetIds));
        for (const f of files) form.append('files', f);
        xhr.open('POST', `/api/v1/cards/${cardId}/comments`);
        const token = localStorage.getItem('token');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (!data.success) return reject(new Error(data.error || 'Upload failed'));
            resolve(data.data);
          } catch (e) { reject(e); }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(form);
      });
    }
    set((s) => ({ comments: [...s.comments, comment] }));
    bumpCardCommentCount(cardId, 1);
    return comment;
  },

  updateComment: async (id, content) => {
    const comment = await authFetch(`/api/v1/comments/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
    set((s) => ({ comments: s.comments.map((c) => (c.id === id ? comment : c)) }));
    return comment;
  },

  deleteComment: async (id) => {
    const target = get().comments.find((c) => c.id === id);
    // conta o deletado + replies que serao apagados em cascata
    const cascade = get().comments.filter((c) => c.id === id || c.parent_id === id).length;
    await authFetch(`/api/v1/comments/${id}`, { method: 'DELETE' });
    set((s) => ({ comments: s.comments.filter((c) => c.id !== id && c.parent_id !== id) }));
    if (target) bumpCardCommentCount(target.card_id, -cascade);
  },
}));

export default useCardPanelStore;
